import { sdkUtils, type Category, type Location } from '@koala/sdk';
import Link from 'next/link';
import styled from 'styled-components';
import { useDispatch, useSelector } from '@/redux';
import { useConfigOverride } from '@/redux/cmsConfig';
import { getFontStyles } from '@/redux/cmsConfig/utils';
import menuActions from '@/redux/menu/actions';
import { supportsBrandId } from '@/utils/config';
import { computeAccessibleTabColors } from '@/utils/contrast';
import { fireGaEvent, gaActions, gaCats } from '@/utils/googleAnalytics';
import { deriveCategoryNavId, updateStoreMenuUrl } from '@/utils/menu';

interface Props {
  categories: Category[];
  locationDetail: Location;
  activeTagIds: number[] | null;
  context: string;
}

export function List({ categories, locationDetail, activeTagIds, context }: Props) {
  const dispatch = useDispatch();
  const { navigationStatus } = useSelector((state) => state.app.menu);
  const { webConfig } = useSelector((state) => state.app.cmsConfig);
  const useBrandId = supportsBrandId(webConfig);

  const { category_nav_background_color, category_nav_font, category_nav_text_hover_color } =
    useConfigOverride('menu_categories');
  const { inactiveOpacity, inactiveColor, activeColor } = computeAccessibleTabColors(
    category_nav_font.color ?? '#1a1a1a',
    category_nav_text_hover_color,
    category_nav_background_color,
  );

  return (
    <>
      {categories.map((category, index) => {
        const url = updateStoreMenuUrl(
          {
            // @ts-expect-error id can't be undefined, which is a valid value for `locationDetail.brand_id`
            id: useBrandId ? locationDetail.brand_id : locationDetail.id,
            name: locationDetail.label,
            catId: String(category.id),
            catName: category.name,
          },
          activeTagIds,
        );

        const isActive = navigationStatus ? String(category.id) === navigationStatus : index === 0;

        return (
          <Link
            href={url}
            replace
            shallow
            passHref
            key={`${sdkUtils.slugName(category)}-${index}`}
            legacyBehavior
          >
            <StyledMenuNavLink
              data-testid="menu-navigation-category"
              id={deriveCategoryNavId(String(category.global_id), context)}
              $active={isActive}
              $inactiveOpacity={inactiveOpacity}
              $inactiveColor={inactiveColor}
              $activeColor={activeColor}
              aria-current={isActive ? 'true' : undefined}
              onClick={(e) => {
                e.preventDefault();
                fireGaEvent(gaCats.browse, gaActions.clickCategory, {
                  label: category.name,
                });
                dispatch(menuActions.requestNavigationUpdate(String(category.id)));
                window.history.pushState(null, '', url);
              }}
            >
              <span>{category.name}</span>
            </StyledMenuNavLink>
          </Link>
        );
      })}
    </>
  );
}
interface StyledMenuNavLinkProps {
  $active?: boolean;
  $inactiveOpacity?: number;
  $inactiveColor?: string;
  $activeColor?: string;
}

export const StyledMenuNavLink = styled.a<StyledMenuNavLinkProps>(
  ({ theme, $active = false, $inactiveOpacity = 0.6, $inactiveColor, $activeColor }) => ({
    borderBottom: '3px solid transparent',
    borderTop: '3px solid transparent',
    cursor: 'pointer',
    opacity: $active ? 1 : $inactiveOpacity,
    padding: '5px 10px 5px 0',
    textAlign: ['left', 'column'].includes(theme.menu_categories.category_nav_display)
      ? 'left'
      : 'center',
    transition: 'all 0.2s linear',
    ...getFontStyles(theme.menu_categories.category_nav_font, {
      color: (value) => {
        if ($active) {
          return $activeColor ?? theme.menu_categories.category_nav_text_hover_color;
        }

        return $inactiveColor ?? value;
      },
    }),
    borderBottomColor: $active
      ? $activeColor ?? theme.menu_categories.category_nav_text_hover_color
      : 'transparent',

    '&:hover': {
      borderBottomColor: $activeColor ?? theme.menu_categories.category_nav_text_hover_color,
      color: $activeColor ?? theme.menu_categories.category_nav_text_hover_color,
      opacity: 1,
      textDecoration: 'none',
    },
  }),
);
